import {
    ApiCadAdditionalInfo,
    BoloAdditionalInfo,
    CadNextNotificationBundle,
    EventQueueView,
    NudgeObjectDto,
    PhoneCallDto,
    ResponsePlanNotification,
    ScheduledEventQueueView,
    UnitQueueView,
    ExportsStreamMessageTypeEnumType,
    UnitGpsDto,
    HospitalQueueView,
    UserAssignmentBundle,
    EventPanelView,
    UpdateUnitQueueBundle,
    UpdateEventQueueBundle,
    EventQueuesBundle,
    UnitQueuesBundle,
    BoloQueueView,
    BoloQueuesBundle,
    BoloPanelView,
    UnitEmergencyDto,
    MapViewBundle,
    FederatedSearchQueryBundle,
    UnitLocationBootstrapResponse,
    FederatedSearchUserQueryMetadata,
    RestartNotificationResponse,
    AmlPhoneCallDto,
    BasicAttachmentDto,
} from '@mark43/cad-api';

import { RawQueueRowData } from '../queues/store/types-2';

export type CautionLevelAttrId = 222000 | 222001 | 222002 | 222003;

export type EventQueueRowData = Partial<EventQueueView> & RawQueueRowData;

// remove Omit in https://mark43.atlassian.net/browse/CAD-22042
export type UnitQueueRowData = Partial<Omit<UnitQueueView, 'backingUpUnitId'>> &
    RawQueueRowData & {
        // dummy properties to satisfy typings of priority-cell
        assignedUnits?: undefined;
        maxUnassignedTimeReached?: undefined;
    };

export type ScheduledEventQueueRowData = RawQueueRowData & Partial<ScheduledEventQueueView>;

export type HospitalQueueRowData = RawQueueRowData & Partial<HospitalQueueView>;

export type BoloQueueRowData = RawQueueRowData & Partial<BoloQueueView>;

export const ProQaNotificationSeverities = {
    DEFAULT: 'default',
    ERROR: 'error',
} as const;

export type ProQaNotificationSeverity =
    (typeof ProQaNotificationSeverities)[keyof typeof ProQaNotificationSeverities];

type BaseProQaEventsMessage = {
    actingUserId: number;
    departmentId: number;
    correlationId?: string;
};

export type ProQaSecondaryEventPanelMetadata = {
    eventId: number;
    parentAgencyEventId: number;
    incidentNumber?: string;
    overIncidentNumber?: string;
    targetCadDiscipline: string;
};

type ProQaOpenSecondaryEventPanelsMessage = BaseProQaEventsMessage & {
    action: 'OPEN_SECONDARY_EVENT_PANELS';
    metadata: {
        secondaryEvents: ProQaSecondaryEventPanelMetadata[];
        shouldFocusPanel?: boolean;
    };
};

type ProQaShowNotificationMessage = BaseProQaEventsMessage & {
    action: 'SHOW_NOTIFICATION';
    metadata: {
        message?: string;
        severity?: ProQaNotificationSeverity;
    };
};

export type ProQaEventsMessage =
    | ProQaOpenSecondaryEventPanelsMessage
    | ProQaShowNotificationMessage;

/**
 * A map from the name of a stream to the shape of payload for message
 */
export type StreamMap = {
    nudge: NudgeObjectDto;
    infos: {
        reset?: number[];
        create?: (ApiCadAdditionalInfo & { text?: string })[];
        delete?: { agencyEventId: number; id: number }[];
        update?: (ApiCadAdditionalInfo & { text?: string })[];
    };
    boloAdditionalInfo: {
        boloId: number;
        boloLinkId: number;
        boloAdditionalInfo: BoloAdditionalInfo;
    };
    serviceRotationNotifications: {
        agencyEventId: number;
        departmentId: number;
    };
    cadNextPhoneCalls: PhoneCallDto;
    cadNextNotifications: CadNextNotificationBundle;
    proqa: ProQaEventsMessage;
    cadNextAml: { context: AmlPhoneCallDto };
    dex_nudge: number;
    responsePlanNotifications: ResponsePlanNotification;
    externalActionNotifications: {
        message: string;
        severity: 'DEFAULT' | 'ERROR';
        createdByUserId: number;
    };
    cadExports: { ids: string[]; exportsStreamMessageType: ExportsStreamMessageTypeEnumType };
    dexInquiryFailedNotifications: {
        userId: number;
        message: string;
    };
    gps: UnitGpsDto;
    userAssignment: UserAssignmentBundle;
    eventPanelUpdates: {
        eventPanelViews: EventPanelView[];
    };
    updateUnitQueue: UpdateUnitQueueBundle;
    updateEventQueue: UpdateEventQueueBundle;
    eventQueues: EventQueuesBundle;
    unitQueues: UnitQueuesBundle;
    boloQueues: BoloQueuesBundle;
    boloPanelView: BoloPanelView;
    unitEmergencyDto: UnitEmergencyDto;
    mapViews: MapViewBundle;
    mapViewsNoCjis: MapViewBundle;
    unitLocationBundles: UnitLocationBootstrapResponse;
    federatedSearchUserQueryMetadata: FederatedSearchUserQueryMetadata;
    userFederatedSearchQueryBundles: FederatedSearchQueryBundle;
    eventFederatedSearchQueryBundles: FederatedSearchQueryBundle;
    restartApplicationNotifications: RestartNotificationResponse;
    cadAttachments: {
        attachments: BasicAttachmentDto[];
    };
};
